// externals
import { connect } from 'react-redux';

// selectors
import * as tradeInSelectors from '../../../store/mmd/tradeIn';
import * as tradeInComponentActionCreators from '../../../store/actionCreators';

// actions
import * as tradeInActionCreators from '../../../store/actionCreators';

// consts/enums
import * as tradeInRoutes from '../../../utils/routes';
import { processStepperRoutes } from '../../../utils/tradeInDecisionLogic';
import {
    LOAN_BALANCE_OPTION_TYPES,
    STEPPER_ROUTES,
    LOAN_BALANCE_TRADE_IN_STEP,
    stepperRouteMap,
    LOAN_BALANCE_STEPS
} from '../../../utils/constants';
import { isShowroomExperience } from '../../../utils/configUtils';

// components
import LoanConfirmationUI from './LoanConfirmationUI';

// interfaces/types
import { ILoanConfirmationUIStateProps, ILoanConfirmationUIDispatchProps } from './LoanConfirmationInterfaces';
import { IRootProps } from '../../../components/RootProps';
import { StepperRoute } from '../../../types';
import { formatDollarsAndCents } from '../../../store/mmd/formatUtils';

export const mapStateToProps = (state: any, ownProps: IRootProps): ILoanConfirmationUIStateProps => {
    const { tradeIn } = tradeInSelectors.getTradeInState(state, ownProps.tradeIndex);
    const isAmdLienPayoffQuoteSpOnSPEnabled = tradeInSelectors.isAmdLienPayoffQuoteEnabled(state);
    const isSalesView = isShowroomExperience(ownProps.config);
    const year = tradeInSelectors.getTradeInVehicleYear(tradeIn);
    const make = tradeInSelectors.getTradeInVehicleMakeName(tradeIn);
    const model = tradeInSelectors.getTradeInVehicleModelName(tradeIn);
    const trim = tradeInSelectors.getTradeInVehicleTrimName(tradeIn);
    const vin = tradeInSelectors.getTradeInVehicleVin(tradeIn) || '--';
    const loanBalanceAmountOwed = tradeInSelectors.getTradeInAmountOwed(tradeIn);
    const loanBalance = loanBalanceAmountOwed && loanBalanceAmountOwed > 0 ? formatDollarsAndCents(loanBalanceAmountOwed) : '--';
    const lenderName = tradeInSelectors.getTradeInAmountOwedSource(tradeIn);
    const vehicleName = `${year} ${make} ${model}`;
    const selectedLoanBalanceOption = tradeInSelectors.getTradeInSelectedLoanBalanceOption(tradeIn);

    const tradeInSource = tradeIn?.source;
    const selectedStepperRoute: StepperRoute[] = stepperRouteMap[tradeInSource] || STEPPER_ROUTES.loanBalance;
    const stepperRoutes = processStepperRoutes(
        state,
        selectedStepperRoute,
        isSalesView,
        isAmdLienPayoffQuoteSpOnSPEnabled,
        ownProps.tradeIndex,
        LOAN_BALANCE_TRADE_IN_STEP,
        tradeInRoutes.TRADE_LOAN_CONFIRMATION
    );

    const pageTitle = 'Trade-In Vehicle';
    const pageSubTitle = 'Determine how much your current vehicle, is worth, and apply that value to your new vehicle.';

    return {
        vehicleName,
        vin,
        trim,
        loanBalance,
        lenderName,
        pageTitle,
        pageSubTitle,
        stepperRoutes,
        selectedLoanBalanceOption,
        tradeIndex: ownProps.tradeIndex,
        urlPath: ownProps.tradeInRoute
    };
};

export const mapDispatchToProps = (dispatch: any): ILoanConfirmationUIDispatchProps => {
    return {
        next: () => {
            // Use thunk to access current state
            dispatch((dispatch: any, getState: any) => {
                const state = getState();
                const isAmdLienPayoffEnabled = tradeInSelectors.isAmdLienPayoffQuoteEnabled(state);

                // Only execute deferred logic if feature flag is enabled
                if (isAmdLienPayoffEnabled) {
                    const tradeInComponent = tradeInSelectors.getTradeInComponent(state);
                    const tradeAddedPayload = (tradeInComponent as any)?.pendingIcoTradeAddedPayload;
                    const tradeCompletedPayload = (tradeInComponent as any)?.pendingIcoTradeCompletedPayload;

                    // Execute deferred logic for EXTERNAL_TRADE_ADDED
                    if (tradeAddedPayload) {
                        const { data, actionType } = tradeAddedPayload;
                        dispatch({ type: actionType, payload: data });
                        dispatch({ type: 'ICO_TRADE_IN_COMPLETED', payload: data });
                    }

                    // Execute deferred logic for EXTERNAL_TRADE_COMPLETED
                    if (tradeCompletedPayload) {
                        dispatch({ type: 'ICO_TRADE_IN_COMPLETED', payload: tradeCompletedPayload });
                        dispatch(tradeInActionCreators.tradeInEstimateSuccess());
                        dispatch(tradeInActionCreators.externalIcoCompleted(true));
                        dispatch(tradeInActionCreators.updateTradeInValuation());
                        dispatch(tradeInActionCreators.sendAddICOAnalytics());
                    }

                    // Clear the pending payloads
                    if (tradeAddedPayload || tradeCompletedPayload) {
                        dispatch({ type: 'CLEAR_ICO_PENDING_PAYLOADS' });
                    }
                }

                dispatch(tradeInActionCreators.cdlLoanConfirmationContinueButtonClicked());
                // Continue with save
                dispatch(tradeInComponentActionCreators.saveOrUpdateTradeIn());
            });
        },
        dispatchAnalytics: (type: string, description: string) => {
            dispatch(tradeInActionCreators.dispatchAnalytics(type, description));
        },
        goToRoute: (route: string) => {
            dispatch(tradeInActionCreators.updateTradeInCurrentLocation(route));
        },
        onEstimateBalanceClick: () => {
            dispatch(tradeInActionCreators.cdlEstimateBalanceLinkClicked(LOAN_BALANCE_STEPS.LOAN_CONFIRMATION));
            dispatch(tradeInActionCreators.updateTradeInCurrentLocation(tradeInRoutes.TRADE_ESTIMATE_LOAN_BALANCE));
        },
        goBack: (selectedLoanBalanceOption) => {
            const backRoute =
                selectedLoanBalanceOption === LOAN_BALANCE_OPTION_TYPES.LOAN_ESTIMATE
                    ? tradeInRoutes.TRADE_ESTIMATE_LOAN_BALANCE
                    : tradeInRoutes.TRADE_FIND_OPEN_LOAN;
            dispatch(tradeInActionCreators.cdlBackButtonClicked(LOAN_BALANCE_TRADE_IN_STEP));
            dispatch(tradeInActionCreators.updateTradeInCurrentLocation(backRoute));
        },
        removeTradeIn: () => {
            // Clear any pending ICO payloads when user declines trade-in (only if feature flag is enabled)
            dispatch((dispatch: any, getState: any) => {
                const state = getState();
                const isAmdLienPayoffEnabled = tradeInSelectors.isAmdLienPayoffQuoteEnabled(state);

                if (isAmdLienPayoffEnabled) {
                    dispatch({ type: 'CLEAR_ICO_PENDING_PAYLOADS' });
                }

                dispatch(tradeInActionCreators.cdlCancelTradeInButtonClicked(LOAN_BALANCE_TRADE_IN_STEP));
                dispatch(tradeInActionCreators.updateTradeInCurrentLocation(tradeInRoutes.TRADE_IN));
                dispatch(tradeInActionCreators.removeTradeIn());
            });
        },
        cdlPageView: () => {
            dispatch(tradeInComponentActionCreators.cdlLoanBalanceFoundPageView());
        },
        cdlTradeInProgressStepperClicked: (analyticsId: string) => {
            dispatch(tradeInComponentActionCreators.cdlTradeInProgressStepperClicked(analyticsId));
        }
    };
};

const LoanConfirmation = connect(mapStateToProps, mapDispatchToProps)(LoanConfirmationUI);

export default LoanConfirmation;
